import React, { useContext } from "react";
import withLanguageContext from "lib/hocs/withLanguageContext";
import useTouchDeviceCheck from "lib/effects/useTouchDeviceCheck";
import { NavTreeContext } from "./context/context";
import { List } from "./list";
import {
  aAppledeviceBlankTarget,
  getCurrentUTCDate,
  getItemStatusInfo,
} from "./functions";

const portalSite = window.location.pathname.split("/")[1];

const ITEM_TYPE_FOLDER = 1;

// Component for the document link
const DocumentLink = ({ item, navTreeId, target, icon, onClick }) => {
  return (
    <a
      target={target}
      href={`/${portalSite}/education/viewdoc.aspx?navtreeid=${navTreeId}&typeid=${item.typeID}`}
      onClick={(e) => onClick(e)}
    >
      <div className="flex align-top break-not-word">
        <div className={icon} />
        <span className={item.typeID === "1" ? "bold" : ""}>{item.title}</span>
      </div>
    </a>
  );
};

// Component for non-clickable folder item
const FolderItem = ({ item, icon, onExpandToggle }) => {
  return (
    <div
      id={item.navTreeID}
      onClick={onExpandToggle}
      className="navtree-item"
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === "Enter") onExpandToggle(e);
      }}
    >
      <div className="flex align-top break-not-word padding-r-40 ">
        <div className={icon} role="button" style={{ zIndex: 0 }} />
        <span className={item.typeID === "1" ? "bold" : ""}>{item.title}</span>
      </div>
    </div>
  );
};

// Component for expand/collapse button
const ExpandButton = ({ expandClass, onExpandToggle, label }) => {
  return (
    <div
      className={expandClass}
      onClick={onExpandToggle}
      title={label}
      aria-label={label}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === "Enter") onExpandToggle(e);
      }}
    />
  );
};

const ListItem = ({
  item,
  expanded,
  active,
  type,
  onClick,
  onExpandToggle,
  languageContext,
}) => {
  const navTreeContext = useContext(NavTreeContext);
  const touchDevice = useTouchDeviceCheck();
  const childNodes = item.childNodes && item.childNodes.length > 0;
  const navTreeId = item.typeID === "0" ? "0" : item.navTreeID;

  const utcDate = getCurrentUTCDate();
  const { itemClass: statusClass, tooltip } = getItemStatusInfo(
    item,
    utcDate,
    languageContext,
  );

  // Determine if the node should be shown
  if (navTreeId === "0") {
    return null;
  }

  // Determine item class
  const itemClass = `${active ? "active" : ""} ${statusClass}`.trim();

  // Determine icon and target
  let icon = item.pathToImage;
  if (item.studentPublish && item.typeID === "1") {
    icon = "navtree-icon-studentfolderclosed";
  }

  const target =
    touchDevice && aAppledeviceBlankTarget(item.typeID)
      ? "_blank"
      : "iFrameMainContent";

  const folderItem = item.typeID === ITEM_TYPE_FOLDER;

  // Determine if this is a hidden folder
  const isFolderItem =
    folderItem &&
    !item.studentPublish &&
    (!navTreeContext.isAdmin || navTreeContext.studentView);

  return (
    <li
      id={item.navTreeID}
      className={itemClass}
      data-type={type}
      title={tooltip}
    >
      {isFolderItem ? (
        <FolderItem item={item} icon={icon} onExpandToggle={onExpandToggle} />
      ) : (
        <DocumentLink
          item={item}
          navTreeId={navTreeId}
          target={target}
          icon={icon}
          onClick={onClick}
        />
      )}

      {childNodes && (
        <>
          <ExpandButton
            expandClass={expanded ? "icon-collapse" : "icon-expand"}
            onExpandToggle={onExpandToggle}
            label={
              expanded ? languageContext("collapse") : languageContext("expand")
            }
          />
          <List
            list={item.childNodes}
            expanded={expanded}
            onClick={onClick}
            onExpandToggle={onExpandToggle}
          />
        </>
      )}
    </li>
  );
};

export default withLanguageContext(ListItem, "NavigationTree");
